/**
 * El micrófono, y lo que deliberadamente no hace con él.
 *
 * Mide el volumen de la habitación y tira la muestra. Nada más. No hay
 * `MediaRecorder`, no hay buffer que se guarde, no hay fichero, no hay red, y no
 * queda ni un byte de audio en ninguna parte. Lo único que sale de aquí es un
 * número entre cero y uno, veinte veces por segundo, que se usa para pintar un
 * color y se olvida.
 *
 * Eso no es una promesa: es la forma del código. `getFloatTimeDomainData`
 * escribe sobre el mismo array una y otra vez y lo que se devuelve es su media
 * cuadrática. No hay ningún sitio donde pudiera acumularse audio aunque se
 * quisiera.
 *
 * El nivel es relativo y no son decibelios de verdad: un micrófono de tablet no
 * está calibrado y cualquier número absoluto sería mentira. Por eso el límite lo
 * pone un adulto mirando la barra, y no viene puesto de fábrica.
 */

export type NoiseSample = (level: number) => void

export interface NoiseMeter {
  stop(): void
}

export type NoiseFailure = 'sin-permiso' | 'sin-microfono'

export type NoiseStart =
  | { readonly ok: true; readonly meter: NoiseMeter }
  | { readonly ok: false; readonly failure: NoiseFailure }

/** Veinte lecturas por segundo: suficiente para que se vea vivo, poco para la batería. */
const INTERVALO_MS = 50

/** Cuánto grita un niño de ocho años a un metro, más o menos, en RMS. */
const ESCALA = 0.3

export async function startNoiseMeter(onSample: NoiseSample): Promise<NoiseStart> {
  if (!navigator.mediaDevices?.getUserMedia) return { ok: false, failure: 'sin-microfono' }

  let stream: MediaStream
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        // Nada de procesado: no se quiere una voz bonita, se quiere saber
        // cuánto ruido hay. La cancelación de eco y el control automático de
        // ganancia falsearían justo lo que se mide.
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
      },
    })
  } catch {
    return { ok: false, failure: 'sin-permiso' }
  }

  // El prefijo de WebKit sigue haciendo falta en Safari antiguo, y esta app se
  // instala en tablets que nadie va a actualizar por gusto.
  const conPrefijo = window as Window & { webkitAudioContext?: typeof AudioContext }
  const AudioContextCtor = window.AudioContext ?? conPrefijo.webkitAudioContext
  if (!AudioContextCtor) {
    for (const track of stream.getTracks()) track.stop()
    return { ok: false, failure: 'sin-microfono' }
  }

  const context = new AudioContextCtor()
  const source = context.createMediaStreamSource(stream)
  const analyser = context.createAnalyser()
  analyser.fftSize = 1024
  source.connect(analyser)
  // El analizador no se conecta a la salida a propósito: nadie quiere oírse a
  // sí mismo por el altavoz de la tablet.

  const buffer = new Float32Array(analyser.fftSize)

  const timer = setInterval(() => {
    analyser.getFloatTimeDomainData(buffer)
    let suma = 0
    for (const muestra of buffer) suma += muestra * muestra
    const rms = Math.sqrt(suma / buffer.length)
    onSample(Math.min(1, rms / ESCALA))
  }, INTERVALO_MS)

  return {
    ok: true,
    meter: {
      stop() {
        clearInterval(timer)
        source.disconnect()
        for (const track of stream.getTracks()) track.stop()
        void context.close()
      },
    },
  }
}
